import { GetServerSidePropsContext } from 'next'
import qs from 'query-string'
import { v4 } from 'uuid'
import { TokenSet } from '@/auth/session'
import { CookieStore } from '@/auth/session/CookieStore'
import { SessionCache } from '@/auth/session/SessionCache'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import { extractAnonymousIdFromRequest } from '@/services/requests'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { calculateJwtCookieMaxAge, setJwtCookie } from '@/utils/cookies/auth-cookie-utils'
import ClientConfig from './ClientConfig'
import { getProtocol } from './handlers/util'
import { generateCodeChallenge, generateCodeVerifier } from './pkce'
import { setPkceVerifierCookie } from './pkce-cookie'
import { AuthorizeQuery } from './types'

export function setLoginHeaders(res: GetServerSidePropsContext['res']) {
  res.setHeader('Cache-Control', 'no-cache, no-store, max-age=0, must-revalidate')
  res.setHeader('Access-Control-Allow-Headers', '*')
}

export function buildLoginRedirect(
  req: GetServerSidePropsContext['req'],
  res: GetServerSidePropsContext['res'],
  query: GetServerSidePropsContext['query'],
  authRoute: 'login' | 'signup',
  experiments?: ServerExperiments,
): string {
  const config = new ClientConfig()
  const protocol = getProtocol(req)
  const codeVerifier = generateCodeVerifier()

  const anonymousId = extractAnonymousIdFromRequest(req) ?? v4()

  const q: AuthorizeQuery = {
    client_id: config.clientId,
    audience: config.audience,
    redirect_uri: `${protocol}://${req.headers.host}${paths.auth.callback}`,
    scope: config.scope,
    response_type: 'code',
    response_mode: 'query',
    auth_route: authRoute,
    code_challenge: generateCodeChallenge(codeVerifier),
    code_challenge_method: 'S256',
    device_id: anonymousId,
  }

  const returnTo = getItemOrFirstEntry(query.returnTo)
  if (returnTo) {
    const parsed = Buffer.from(returnTo, 'base64').toString('utf-8')
    const fullyQualified = parsed?.startsWith('http') ? parsed : `${protocol}://${req.headers.host}${parsed}`
    q.state = Buffer.from(JSON.stringify({ returnTo: fullyQualified, anonymousId }), 'utf-8').toString('base64url')
  }

  const email = getItemOrFirstEntry(query.email)
  if (email) {
    q.email = email
  }

  if (experiments?.require_demographics_on_signup) {
    q['feature_flags[0]'] = 'demographics'
  }

  setPkceVerifierCookie(codeVerifier, { req, res })

  return `${config.authorizationUri}?${qs.stringify(q)}`
}

/**
 * Given an incoming request URL, derive the canonical page pathname.
 * Handles Next.js data routes like `/_next/data/<buildId>/path.json` by
 * converting them back to `/path`.
 */
function getCleanPathnameFromReqUrl(originalUrl: string): string {
  const rawPathname = (originalUrl || '/').split('?')[0] || '/'
  if (rawPathname.startsWith('/_next/data/') && rawPathname.endsWith('.json')) {
    // Strip the `/_next/data/<buildId>` prefix and the trailing `.json`
    const withoutPrefix = rawPathname.replace(/^\/_next\/data\/[^/]+/, '')
    const withoutSuffix = withoutPrefix.replace(/\.json$/, '')
    return withoutSuffix || '/'
  }
  return rawPathname || '/'
}

/**
 * Processes a `token` query parameter on SSR to establish an authenticated session.
 * - Decodes token for expiry to compute cookie maxAge (falls back to 24h)
 * - Creates a session via SessionCache
 * - Sets a hardened JWT cookie (Secure in prod, SameSite=lax)
 * - Sets no-store cache headers
 * - Returns a redirect object to the provided destination path with the `token` param stripped
 *
 * Usage example in getServerSideProps:
 *   const redirect = await handleTokenParamOnSSR(req, res, query, `/express-checkout/${projectSlug}/checkout/${offerId}`)
 *   if (redirect) return { redirect }
 */
export async function handleTokenParamOnSSR(
  req: GetServerSidePropsContext['req'],
  res: GetServerSidePropsContext['res'],
  query: GetServerSidePropsContext['query'],
): Promise<{ destination: string; permanent: false } | undefined> {
  const token = getItemOrFirstEntry(query?.token)
  if (!token) return undefined

  // Establish session from token
  const maxAge = calculateJwtCookieMaxAge(token)
  const clientConfig = new ClientConfig()
  const cookieStore = new CookieStore(clientConfig)
  const sessionCache = new SessionCache(clientConfig, cookieStore)
  const tokenSet = new TokenSet({ id_token: token, expires_in: maxAge })
  const session = sessionCache.fromTokenSet(tokenSet)
  await sessionCache.create(req, res, session)

  setJwtCookie(token, { req, res })

  // Prevent caching of this response
  res.setHeader('Cache-Control', 'no-cache, no-store, max-age=0, must-revalidate')

  // Build redirect without the token param
  const { token: _omit, ...rest } = query
  const sanitizedQuery = qs.stringify({ ...rest, loginViaTokenParam: 'true' })
  const originalUrl = req.url || '/'
  const pathname = getCleanPathnameFromReqUrl(originalUrl)
  const destination = `${pathname}${sanitizedQuery ? `?${sanitizedQuery}` : ''}`

  return { destination, permanent: false }
}
